import React, { useState } from "react";
import PropTypes from "prop-types";
import { createWorkpaperAggregation, createWorkpaperDocuments } from "../../apiClient/operations/workpapersOperations";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/solid";
import Modal from "../../tailwindUI/Modal";
import PrimaryButton from "../../tailwindUI/PrimaryButton";
import SecondaryButton from "../../tailwindUI/SecondaryButton";
import Badge from "../../tailwindUI/Badge";
import useFormatDate from "../../hooks/useFormatDate";

const documentTypes = {
  "tax_invoices": "Facturas",
  "tax_returns": "Declaraciones mensuales",
  "annual_tax_returns": "Declaraciones anuales",
  "rif_tax_returns": "Declaraciones RIF",
  "tax_status": "Constancia de situación fiscal",
  "tax_retentions": "Retenciones",
  "tax_compliance": "Opinión de cumpliminento",
  "electronic_accounting": "Contabilidad electrónica",
  "rpc": "RPC",
  "buro_de_credito_report": "Reporte de buró de créditco",
  "bil": "Reporte de buró de investigaciones legales"
}

export function CreateWorkpaperDocumentsModal({
  workpaper,
  showModal,
  setShowModal,
  setAlert
}) {
  console.log("workpaper:", workpaper?.updated)
  const { formatDate } = useFormatDate();
  const [isLoadingCreateWp, setIsLoadingCreateWp] = useState(false);
  const [workpaperCreated, setWorkpaperCreated] = useState(null);

  const sendCreateWorkpaperDocuments = async (e) => {
    try {
      e.preventDefault();

      if (isLoadingCreateWp) return;

      setIsLoadingCreateWp(true);

      if (workpaper?.fiscal_documents?.some(document => document?.status == "pending")) {
        const response = await createWorkpaperDocuments(workpaper?.id);
        const extractions = response?.extractions;
        const fiscalDocuments = Object.keys(workpaperCreated?.workpaper?.fiscal_documents || {})?.map(document => ({
          "name": document,
          "status": response?.workapper?.fiscal_documents?.[document]
        }))
        const pendingDocuments = fiscalDocuments?.filter(document => !extractions.includes(document?.name) && document?.status == 'pending')
        setWorkpaperCreated({
          "pending_documents": pendingDocuments,
          "extractions": extractions,
        });
      } else {
        await createWorkpaperAggregation(workpaper?.id);
        setAlert({
          active: true,
          type: "success",
          message: "Se mandó a crear los papeles de trabajo, puede tardar unos minutos.",
        });
        setShowModal(false);
      }

    } catch (error) {
      setAlert({ active: true, type: "error", message: error.message });
      setShowModal(false);
    } finally {
      setIsLoadingCreateWp(false);
    }
  }

  return (
    <>
      <Modal
        show={showModal}
        setShow={setShowModal}
        className="min-w-full sm:min-w-[500px]"
      >
        <div className="py-2 px-2 space-y-3">
          <div className="relative">
            <XMarkIcon
              className="absolute w-7 h-7 -right-3 -top-3 cursor-pointer text-gray-500"
              onClick={() => setShowModal(false)}
            />
          </div>

          {!workpaperCreated ? (
            <>

              <h3 className="text-lg text-center font-medium leading-6 text-gray-900">
                ¿Crear papeles de trabajo?
              </h3>

              {workpaper?.fiscal_documents?.some(document => document?.status == "pending") ? (
                <div>
                  <div className="mb-4 text-gray-700">
                    Hay documentos pendientes de descarga. Si continúas, se cancelarán y se reiniciará el proceso para generar los papeles de trabajo.
                  </div>
                  <div className="py-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6 justify-between">
                    <dt className="text-sm font-medium text-gray-500">
                      Fecha de actualización
                    </dt>
                    <dd className="mt-1 text-sm text-gray-500 sm:mt-0">
                      {formatDate(workpaper?.updated, 'DD MMM YYYY, HH:mm:ss')}
                    </dd>
                  </div>
                  {workpaper?.fiscal_documents?.filter(document => document?.status == "pending")?.map(document => (
                    <div key={document.name} className="py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 justify-between">
                      <dt className="text-sm font-medium text-gray-500 col-span-2">{documentTypes?.[document?.name] || document?.name}</dt>
                      <dd className="mt-1 text-sm text-gray-500 sm:mt-0">
                        <Badge text={document?.status} />
                      </dd>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mb-4 text-gray-700">
                  Se enviará la creación de papeles de trabajo, puede tardar unos minutos.
                </div>
              )}

              <form onSubmit={sendCreateWorkpaperDocuments}>
                <div className="w-fit mt-6 ml-auto space-x-2">
                  <SecondaryButton
                    type="button"
                    onClick={() => setShowModal(false)}
                    disabled={isLoadingCreateWp}
                  >
                    Cancelar
                  </SecondaryButton>
                  <PrimaryButton type="submit" disabled={isLoadingCreateWp}>
                    Crear
                  </PrimaryButton>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className='flex justify-center'>
                <CheckCircleIcon className='w-20 h-20 text-green-600' />
              </div>
              <h3 className="text-lg text-center font-medium leading-6 text-gray-900">
                Completado
              </h3>

              {workpaperCreated?.workpaperCreated?.some(document => document.stats == "pending") && (
                <div>
                  <div className="mb-4 text-gray-700">
                    Documentos pendiente de descarga
                  </div>
                  {workpaperCreated?.workpaperCreated?.map(document => (
                    <div key={document.name} className="py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 justify-between">
                      <dt className="text-sm font-medium text-gray-500 col-span-2">{documentTypes?.[document?.name] || document}</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0">
                        <Badge text={workpaperCreated?.workpaper?.fiscal_documents[document?.status]} />
                      </dd>
                    </div>
                  ))}
                </div>
              )}

              {(workpaperCreated?.extractions && workpaperCreated?.extractions?.length > 0) && (
                <div>
                  <div className="mb-4 text-gray-700">
                    Se reinició la descarga de los siguientes documentos. La demora dependerá de la cantidad de información a descargar
                  </div>
                  {workpaperCreated?.extractions?.map(document => (
                    <div key={document} className="py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 justify-between">
                      <dt className="text-sm font-medium text-gray-500 col-span-2">{documentTypes?.[document] || document}</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0">
                        <Badge text="pending" />
                      </dd>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
}

CreateWorkpaperDocumentsModal.propTypes = {
  workpaper: PropTypes.object,
  showModal: PropTypes.bool,
  setShowModal: PropTypes.func,
  setAlert: PropTypes.func
};
